import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productService } from '../services/product.service';
import { ProductCard } from '../components/shop/ProductCard';
import { Button } from '../components/ui/Button';
import { Heart, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useToast } from '../components/ui/ToastProvider';

export const WishlistPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const { data: wishlistItems, isLoading } = useQuery({
    queryKey: ['wishlist'],
    queryFn: productService.getWishlist,
  });

  const removeMutation = useMutation({
    mutationFn: productService.removeFromWishlist,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wishlist'] });
      showToast('Removed from wishlist', 'info');
    },
    onError: () => {
      showToast('Failed to remove from wishlist', 'error');
    },
  });

  const handleWishlistToggle = (productId: number) => {
    removeMutation.mutate(productId);
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!wishlistItems || wishlistItems.length === 0) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-md mx-auto text-center">
          <Heart className="mx-auto h-16 w-16 text-[rgb(var(--light-secondary))]" />
          <h2 className="mt-4 text-2xl font-bold text-[rgb(var(--foreground))]">Your wishlist is empty</h2>
          <p className="mt-2 text-[rgb(var(--light-secondary))]">
            Save your favorite items to buy later
          </p>
          <Link to="/shop">
            <Button variant="primary" className="mt-6">
              Browse Products
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-[rgb(var(--foreground))]">My Wishlist</h1>
      <p className="text-[rgb(var(--light-secondary))]">{wishlistItems.length} items saved</p>

      <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {wishlistItems.map((product) => (
          <ProductCard
            key={product.id}
            product={product}
            onWishlistToggle={handleWishlistToggle}
            inWishlist={true}
          />
        ))}
      </div>
    </div>
  );
};